<template>
  <div class="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg overflow-hidden">
    <div class="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-2">
        <ChartBarSquareIcon class="h-5 w-5 text-purple-500" />
        <h3 class="text-lg font-medium text-gray-900 dark:text-white">
          Funnelverdeling per Afdeling
        </h3>
        <button
          @click="showInfo = !showInfo"
          class="text-gray-400 hover:text-purple-500 dark:hover:text-purple-400 transition-colors"
          title="Uitleg over de afdelingsvergelijking"
        >
          <InformationCircleIcon class="h-5 w-5" />
        </button>
      </div>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Gewogen omzet per fase per afdeling — vergelijk funnelverloop
      </p>
    </div>

    <InfoBox v-if="showInfo" title="Hoe lees je deze grafiek?" color="purple">
      <p>
        Elke lijn representeert een afdeling. De X-as toont de salesfases (Verkenning → Lead →
        Prospect). De Y-as toont de <strong>gewogen omzet</strong> (dealwaarde × ML-winkans).
      </p>
      <p>
        <strong>Stijgende lijnen</strong> wijzen op meer pipeline in latere fasen (dichter bij
        winnen). <strong>Dalende lijnen</strong> wijzen op veel vroege pipeline die nog moet
        converteren.
      </p>
    </InfoBox>

    <div class="p-5">
      <VChart
        :option="chartOption"
        :notMerge="true"
        autoresize
        style="width: 100%; height: 300px"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { ChartBarSquareIcon, InformationCircleIcon } from '@heroicons/vue/24/outline'
import InfoBox from './InfoBox.vue'

use([LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

const props = defineProps({
  data: { type: Object, required: true }
})

const showInfo = ref(false)

const isDarkMode = ref(false)

const updateDarkMode = () => {
  isDarkMode.value = document.documentElement.classList.contains('dark')
}

let observer = null

onMounted(() => {
  updateDarkMode()
  observer = new MutationObserver(updateDarkMode)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

const TEAM_COLORS = [
  '#3b82f6',
  '#8b5cf6',
  '#06b6d4',
  '#f59e0b',
  '#ef4444',
  '#10b981',
  '#f97316',
  '#84cc16',
  '#e11d48',
  '#14b8a6'
]

const stages = ['Verkenning', 'Lead', 'Prospect']

function formatCurrency(amount) {
  if (!amount) return '€ 0'
  return `€ ${new Intl.NumberFormat('nl-NL').format(Math.round(amount))}`
}

const chartOption = computed(() => {
  const dark = isDarkMode.value
  const colors = {
    textPrimary: dark ? '#e5e7eb' : '#374151',
    textSecondary: dark ? '#9ca3af' : '#6b7280',
    border: dark ? '#4b5563' : '#e5e7eb',
    tooltipBg: dark ? '#1f2937' : '#ffffff',
    tooltipBorder: dark ? '#374151' : '#e5e7eb'
  }

  const teams = props.data.teams
  const series = teams.map((team, i) => ({
    name: team.team_name,
    type: 'line',
    smooth: true,
    symbol: 'circle',
    symbolSize: 8,
    data: [
      team.subtotals.verkenning_weighted,
      team.subtotals.lead_weighted,
      team.subtotals.prospect_weighted
    ],
    lineStyle: { width: 3, color: TEAM_COLORS[i % TEAM_COLORS.length] },
    itemStyle: { color: TEAM_COLORS[i % TEAM_COLORS.length] },
    emphasis: { focus: 'series' }
  }))

  return {
    animation: true,
    grid: { left: 80, right: 30, top: 50, bottom: 40 },
    tooltip: {
      trigger: 'axis',
      appendToBody: true,
      backgroundColor: colors.tooltipBg,
      borderColor: colors.tooltipBorder,
      textStyle: { color: colors.textPrimary, fontSize: 12 },
      extraCssText: 'z-index: 9999;',
      position: (point, params, dom, rect, size) => {
        const chartRect = rect || { x: 0, y: 0 }
        const viewportX = chartRect.x + point[0]
        const viewportY = chartRect.y + point[1]
        const x = viewportX + 15
        const y = Math.max(10, viewportY - size.contentSize[1] / 2)
        const maxX = window.innerWidth - size.contentSize[0] - 20
        return [Math.min(x, maxX), y]
      },
      formatter: params => {
        if (!params?.length) return ''
        const stageName = params[0].axisValue
        let html = `<div style="font-weight:600;margin-bottom:6px;">${stageName}</div>`
        const sorted = [...params].sort((a, b) => (b.value || 0) - (a.value || 0))
        for (const p of sorted) {
          html += `<div style="display:flex;align-items:center;gap:6px;margin:3px 0;">
            <span style="display:inline-block;width:10px;height:10px;background:${p.color};border-radius:50%;"></span>
            <span>${p.seriesName}</span>
            <span style="margin-left:auto;font-weight:500;">${formatCurrency(p.value)}</span>
          </div>`
        }
        return html
      }
    },
    legend: {
      top: 0,
      type: 'scroll',
      textStyle: { color: colors.textPrimary, fontSize: 12 },
      pageTextStyle: { color: colors.textSecondary },
      pageIconColor: colors.textSecondary,
      pageIconInactiveColor: colors.border
    },
    xAxis: {
      type: 'category',
      data: stages,
      boundaryGap: false,
      axisLabel: { color: colors.textPrimary, fontSize: 12 },
      axisLine: { lineStyle: { color: colors.border } },
      axisTick: { lineStyle: { color: colors.border } }
    },
    yAxis: {
      type: 'value',
      name: 'Gewogen omzet',
      nameTextStyle: { fontSize: 11, color: colors.textSecondary },
      axisLabel: {
        color: colors.textSecondary,
        fontSize: 11,
        formatter: v => {
          if (v >= 1000000) return `€${(v / 1000000).toFixed(1)}M`
          if (v >= 1000) return `€${(v / 1000).toFixed(0)}K`
          return `€${v}`
        }
      },
      axisLine: { lineStyle: { color: colors.border } },
      splitLine: { lineStyle: { color: colors.border, type: 'dashed' } }
    },
    series
  }
})
</script>
